import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiThumbsUp, FiBriefcase, FiStar } = FiIcons;

const reasons = [
  {
    icon: FiMail,
    title: 'Mail Gets Held',
    body: 'A physical mail piece gets picked up, carried inside, and set on the counter. A Facebook post is scrolled past in 0.3 seconds and forgotten.'
  },
  {
    icon: FiThumbsUp,
    title: 'It Builds Instant Trust',
    body: 'When someone sees your name on a professionally printed mailer, they take you seriously. It signals that you invest in your business — and that you\'re not going anywhere.'
  },
  {
    icon: FiBriefcase,
    title: 'You Look Established',
    body: 'Anyone can post on social media for free. Not everyone mails 5,000 homes. That difference registers immediately with the people receiving it.'
  },
  {
    icon: FiStar,
    title: 'Neighbors Refer It',
    body: 'A mailer sitting on the fridge gets pointed to when someone asks "do you know a good caterer?" No algorithm needed — just visibility at the right moment.'
  }
];

export default function WhyMailWorks() {
  return (
    <section className="py-24 bg-brand-dark overflow-hidden relative">
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-blue rounded-full blur-[100px]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block py-1.5 px-4 rounded-full bg-brand-blue/20 text-brand-blue text-xs font-black tracking-widest mb-6 uppercase border border-brand-blue/30">
            Why Direct Mail Still Wins
          </span>
          <h2 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tighter italic leading-none mb-6">
            People Trust <br className="hidden md:block"/> What They Can Touch.
          </h2>
          <p className="text-xl text-brand-cream/60 font-bold max-w-2xl mx-auto leading-relaxed">
            Social media is noise. A mailer in someone's hand is a statement — one that says your business is established, professional, and worth calling when something needs fixing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {reasons.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="flex gap-6 bg-white/5 border border-white/10 rounded-[2rem] p-8 hover:border-brand-blue/40 transition-all group"
            >
              <div className="w-14 h-14 shrink-0 bg-brand-blue/10 rounded-2xl flex items-center justify-center text-brand-blue text-2xl group-hover:bg-brand-blue group-hover:text-white transition-all">
                <SafeIcon icon={r.icon} />
              </div>
              <div>
                <h3 className="text-white font-black uppercase tracking-tight text-lg mb-2">{r.title}</h3>
                <p className="text-brand-cream/50 font-bold leading-relaxed">{r.body}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 bg-brand-blue/10 border border-brand-blue/20 rounded-[2rem] p-8 md:p-10 text-center">
          <p className="text-brand-cream/80 font-black text-xl md:text-2xl leading-relaxed max-w-3xl mx-auto">
            "When people see your name on a mail piece, they take you seriously over a random post on Facebook. <span className="text-brand-yellow">It tells them you take your business seriously.</span> That trust is worth more than any algorithm."
          </p>
          <p className="text-brand-cream/30 text-xs font-bold uppercase tracking-widest mt-4">— Sean, Mailbox Digital</p>
        </div>
      </div>
    </section>
  );
}
